/**
 * The single-scene preview transport (spec 009), shared by the story pane's
 * single-scene mode (`SingleScenePreview`) and the scene studio (spec 029):
 * ▶ Play / ⏸ Pause, an optional ⏹ Stop and "⏮ From start", the time display
 * and a scrub bar.
 *
 * It holds no playback state: the caller says whether it is playing and
 * where the playhead is (`playheadMs`, `lengthMs`, ms on the runtime clock),
 * and does the playing in the callbacks. A press on the scrub bar calls
 * `onScrubStart`, then `onScrub(ms)` for the press and every move, then
 * `onScrubEnd` on release; the caller pauses and resumes around it.
 *
 * Test ids: `preview-time` (exact ms in `data-ms`), `preview-scrub`,
 * `preview-from-start`.
 */

import React, { useCallback, useRef } from 'react';
import styles from '../StoryPreviewPane.module.scss';
import PlayFromStartIcon from './PlayFromStartIcon';

export function formatPreviewTime(ms: number): string {
  return `${(Math.max(0, ms) / 1000).toFixed(1)}s`;
}

export interface PreviewTransportProps {
  /** True shows ⏸ Pause, false ▶ Play. */
  playing: boolean;
  playheadMs: number;
  lengthMs: number;
  onPlay: () => void;
  onPause: () => void;
  /** ⏹ Stop; omitted, no Stop button. */
  onStop?: () => void;
  stopDisabled?: boolean;
  /** "⏮ From start"; omitted, no such button. */
  onFromStart?: () => void;
  onScrubStart?: () => void;
  /** Show the frame at `ms` (0 to `lengthMs`). */
  onScrub: (ms: number) => void;
  onScrubEnd?: () => void;
  className?: string;
  'data-testid'?: string;
}

export const PreviewTransport: React.FC<PreviewTransportProps> = ({
  playing,
  playheadMs,
  lengthMs,
  onPlay,
  onPause,
  onStop,
  stopDisabled = false,
  onFromStart,
  onScrubStart,
  onScrub,
  onScrubEnd,
  className,
  'data-testid': testId,
}) => {
  const barRef = useRef<HTMLDivElement>(null);
  const callbacks = useRef({ onScrub, onScrubEnd, lengthMs });
  callbacks.current = { onScrub, onScrubEnd, lengthMs };

  const scrubTo = useCallback((clientX: number) => {
    const bar = barRef.current;
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    const pct = rect.width > 0 ? Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)) : 0;
    callbacks.current.onScrub(pct * callbacks.current.lengthMs);
  }, []);

  const handleDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    onScrubStart?.();
    scrubTo(e.clientX);
    const onMove = (ev: MouseEvent) => scrubTo(ev.clientX);
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      callbacks.current.onScrubEnd?.();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [onScrubStart, scrubTo]);

  const pct = lengthMs > 0 ? Math.min(100, (playheadMs / lengthMs) * 100) : 0;

  return (
    <div className={className ?? styles.transport} data-testid={testId}>
      <div className={styles.controls}>
        {playing ? (
          <button type="button" className={styles.controlBtn} onClick={onPause} title="Pause" aria-label="Pause">⏸</button>
        ) : (
          <button type="button" className={styles.controlBtn} onClick={onPlay} title="Play" aria-label="Play">▶</button>
        )}
        {onFromStart && (
          <button
            type="button"
            className={styles.controlBtn}
            onClick={onFromStart}
            title="From start"
            aria-label="Play from the start"
            data-testid="preview-from-start"
          ><PlayFromStartIcon size={14} /></button>
        )}
        {onStop && (
          <button
            type="button"
            className={styles.controlBtn}
            onClick={onStop}
            title="Stop"
            aria-label="Stop"
            disabled={stopDisabled}
          >⏹</button>
        )}
        <span className={styles.timeDisplay} data-testid="preview-time" data-ms={Math.round(playheadMs)}>
          {formatPreviewTime(playheadMs)} / {formatPreviewTime(lengthMs)}
        </span>
      </div>
      <div
        className={styles.timeline}
        ref={barRef}
        onMouseDown={handleDown}
        data-testid="preview-scrub"
      >
        <div className={styles.timelineFill} style={{ width: `${pct}%` }} />
        <div className={styles.timelineHead} style={{ left: `${pct}%` }} />
      </div>
    </div>
  );
};

export default PreviewTransport;
